import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import type { DriveItem } from "@/api/driveApi";
import { useProtectPdf } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "../toast/ToastProvider";

/** Password-protect / edit-lock a PDF — writes a protected copy next to it. */
export function ProtectPdfDialog({ item, onClose }: { item: DriveItem; onClose: () => void }) {
    const protect = useProtectPdf();
    const toast = useToast();
    const navigate = useNavigate();
    const [userPw, setUserPw] = useState("");
    const [ownerPw, setOwnerPw] = useState("");
    const [editLock, setEditLock] = useState(false);
    const busy = protect.isPending;

    const submit = async () => {
        if (!userPw && !ownerPw) return;
        try {
            const out = await protect.mutateAsync({ id: item.id, userPassword: userPw, ownerPassword: ownerPw, editLock });
            onClose();
            toast.success(`Created protected copy “${out.name}”`, {
                action: {
                    label: "Show",
                    onClick: () => navigate(`${out.parentId ? `/folder/${encodeURIComponent(out.parentId)}` : "/"}?select=${encodeURIComponent(out.id)}`)
                }
            });
        } catch (e) {
            toast.error(e);
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            dismissable={!busy}
            title={`Protect “${item.name}”`}
            description="Creates a new PDF next to the original with your password and permissions applied. The original stays untouched."
            icon={<Lock className="h-5 w-5 text-red-600 dark:text-red-400" />}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={() => void submit()} disabled={busy || (!userPw && !ownerPw)}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                        Protect
                    </Button>
                </>
            }
        >
            <form
                className="space-y-3"
                onSubmit={(e) => {
                    e.preventDefault();
                    void submit();
                }}
            >
                <div>
                    <label htmlFor="protect-open-pw" className="mb-1 block text-xs font-medium text-muted-foreground">Open password</label>
                    <Input id="protect-open-pw" type="password" value={userPw} onChange={(e) => setUserPw(e.target.value)} placeholder="Required to open the PDF" data-autofocus="" autoComplete="new-password" />
                </div>
                <div>
                    <label htmlFor="protect-owner-pw" className="mb-1 block text-xs font-medium text-muted-foreground">Owner password (optional)</label>
                    <Input id="protect-owner-pw" type="password" value={ownerPw} onChange={(e) => setOwnerPw(e.target.value)} placeholder="Required to change permissions later" autoComplete="new-password" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="h-4 w-4 accent-primary" checked={editLock} onChange={(e) => setEditLock(e.target.checked)} />
                    Edit lock — disable printing, copying and editing
                </label>
                <button type="submit" hidden aria-hidden tabIndex={-1} />
            </form>
        </Dialog>
    );
}
